import { Portal } from '@radix-ui/react-portal';
import Snackbar, {
  SnackbarButton,
  SnackbarType,
} from '@superfunnel/ui-core/Snackbar';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { uid } from 'uid';
import { parseFirebaseError } from '../utils/error-utils';

const CLOSE_AUTO = 5000;
const CLOSE_ANIMATION_DURATION = 500;

const overlays = document.getElementById('overlays')!;

interface SnackbarItem {
  id: string;
  type: SnackbarType;
  message: string;
  shownAt: Date;
  show: boolean;
  disableAutoClose: boolean;
  hideCloseButton: boolean;
  buttons?: SnackbarButton[];
}

interface SnackbarConfig {
  disableAutoClose?: boolean;
  hideCloseButton?: boolean; // Optional to hide the close button
  id?: string; // Optional ID to allow custom IDs for snackbars
  buttons?: SnackbarButton[];
}

interface SnackbarContext {
  show: (
    message: string,
    type?: SnackbarType,
    config?: SnackbarConfig,
  ) => SnackbarItem;
  showError: (error: unknown, config?: SnackbarConfig) => SnackbarItem;
  showDebug: (
    message: string,
    type?: SnackbarType,
    config?: SnackbarConfig,
  ) => SnackbarItem | undefined;
  hide: (id?: string | string[]) => void;
  getSnackbarById: (id: string) => SnackbarItem | undefined;
}

export const SnackbarContext = createContext<SnackbarContext>({
  show: () => {
    throw new Error('show() not implemented');
  },
  showError: () => {
    throw new Error('showError() not implemented');
  },
  showDebug: () => {
    throw new Error('showDebug() not implemented');
  },
  hide: () => {},
  getSnackbarById: () => {
    throw new Error('getSnackbarById() not implemented');
  },
});

export function SnackbarContextProvider(props: any) {
  const [snackbars, setSnackbars] = useState<SnackbarItem[]>([]);

  const show = useCallback(
    (message: string, type: SnackbarType = 'info', config?: SnackbarConfig) => {
      const id = config?.id || uid(12);
      const snackbar = {
        id,
        type,
        message,
        shownAt: new Date(),
        show: true,
        disableAutoClose: config?.disableAutoClose || false,
        hideCloseButton: config?.hideCloseButton || false,
        buttons: config?.buttons,
      };
      setSnackbars((prev) => [
        ...prev.filter((s) => s.id !== id), // Remove existing snackbar with the same ID
        snackbar,
      ]);

      return snackbar;
    },
    [],
  );

  const showDebug = useCallback(
    (message: string, type: SnackbarType = 'info') => {
      if (import.meta.env.NODE_ENV !== 'development') return;
      return show(message, type);
    },
    [show],
  );

  const showError = useCallback(
    (error: unknown, config?: SnackbarConfig) => {
      const message = parseFirebaseError(error);
      return show(message, 'warn', { disableAutoClose: true, ...config });
    },
    [show],
  );

  const hide = useCallback((id?: string | string[]) => {
    const isSnackbar = (sb: SnackbarItem) => {
      if (!id) return false;
      if (Array.isArray(id)) return id.includes(sb.id);
      return sb.id === id;
    };
    setSnackbars((prev) =>
      prev.some(isSnackbar)
        ? prev.map((snackbar) => {
            return isSnackbar(snackbar)
              ? { ...snackbar, show: false }
              : snackbar;
          })
        : prev,
    );
  }, []);

  const getSnackbarById = useCallback(
    (id: string) => {
      return snackbars.find((snackbar) => snackbar.id === id);
    },
    [snackbars],
  );

  useEffect(() => {
    if (!snackbars.length) return;

    const autoCloseTimer = setInterval(() => {
      const now = new Date();
      const snackbarsToAutoClose = snackbars.filter(
        (snackbar) =>
          snackbar.show &&
          !snackbar.disableAutoClose &&
          snackbar.shownAt.getTime() + CLOSE_AUTO < now.getTime(),
      );

      if (!snackbarsToAutoClose.length) return;

      setSnackbars((prev) =>
        prev.map((snackbar) => {
          if (snackbarsToAutoClose.some((s) => s.id === snackbar.id)) {
            return { ...snackbar, show: false };
          }

          return snackbar;
        }),
      );
    }, 100);

    const removeHiddenSnackbarsTimer = setInterval(() => {
      const snackbarsToClose = snackbars.filter(
        (snackbar) =>
          !snackbar.show &&
          snackbar.shownAt.getTime() + CLOSE_AUTO + CLOSE_ANIMATION_DURATION <
            new Date().getTime(),
      );

      if (!snackbarsToClose.length) return;

      setSnackbars((prev) =>
        prev.filter(
          (snackbar) => !snackbarsToClose.some((s) => s.id === snackbar.id),
        ),
      );
    }, 100);

    return () => {
      clearInterval(autoCloseTimer);
      clearInterval(removeHiddenSnackbarsTimer);
    };
  }, [snackbars]);

  return (
    <SnackbarContext.Provider
      value={{
        show: show,
        showError: showError,
        showDebug: showDebug,
        hide: hide,
        getSnackbarById: getSnackbarById,
      }}
    >
      <div>{props.children}</div>
      <Portal container={overlays || undefined}>
        <div
          aria-live="assertive"
          className="pointer-events-none fixed inset-0 z-[9999] flex items-end px-4 py-6 sm:items-start sm:p-6"
          onPointerDownCapture={(e) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest('[data-snackbar-card]')) {
              e.stopPropagation();
              e.preventDefault();
            }
          }}
        >
          <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
            {snackbars.map((snackbar) => (
              <Snackbar
                show={snackbar.show}
                key={snackbar.id}
                type={snackbar.type}
                title={snackbar.message}
                hideCloseButton={snackbar.hideCloseButton}
                onClose={() => hide(snackbar.id)}
                buttons={snackbar.buttons}
              />
            ))}
          </div>
        </div>
      </Portal>
    </SnackbarContext.Provider>
  );
}

export const useSnackbarContext = () => {
  return useContext(SnackbarContext);
};
